import { Button, Card, Col, Divider, Progress, Row, Spin } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SupervisionService from "../../../../../../services/SupervisionService";
import { SupervisionDetails } from "../../../../../../models/Supervision";
import Supervision, { SupervisionWarehouse } from "../../../../../../models/Supervision";
import { CaretLeftOutlined, VideoCameraTwoTone } from "@ant-design/icons";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { useLanguage } from "../../../../../../context/LanguageContext";
import CorridorSelectorMap from "../CorridorSelectorMap";
import CorridorHistoryMap from "./CorridorHistoryMap ";

const CorridorHistory: React.FC = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const { warehouseIdx, sweepIdx } = useParams();
  const [supervisions, setsupervisions] = useState<Supervision[]>([]);
  const [supervisionWarehouse, setSupervisionWarehouse] = useState<SupervisionWarehouse>();
  const [corridorsLoading, setCorridorsLoading] = useState<boolean>(true)
  const navigate = useNavigate()
  const screens = useBreakpoint();
  const corridorDetailsCardBodyStyle = useMemo<React.CSSProperties>(
    () => ({
      height: screens.xs ? 320 : 190,
      overflow: "auto",
    }),
    [screens.xs]
  );
  const [corridorFilter, setCorridorFilter] = useState<string>("");
  const [deviceFilter, setDeviceFilter] = useState<string[]>([]);
  const [toShowFilter, setToShowFilter] = useState<number>(1);
  const [selectedCorridorName, setSelectedCorridorName] = useState<string | null>(null);
  const [selectedCorridorDetails, setSelectedCorridorDetails] = useState<SupervisionDetails>();
  const [corridorMapLoading, setCorridorMapLoading] = useState<boolean>(false);

  const getCorridorSeverity = useCallback((supervision: Supervision) => {
    if (supervision.reading_percentage < 0.1) {
      return {
        color: "gold",
        strokeColor: "#fadb14",
      };
    }

    if (supervision.effective_percentage < 0.5) {
      return {
        color: "red",
        strokeColor: "#f5222d",
      };
    }

    if (supervision.effective_percentage < 0.75) {
      return {
        color: "gold",
        strokeColor: "#fadb14",
      };
    }

    return {
      color: "green",
      strokeColor: "#52c41a",
    };
  }, []);
   const onApplyFilters = useCallback(
     (
       device: string[],
       corridor: string,
       toShowFilter: number
     ) => {
       setDeviceFilter(device)
       setCorridorFilter(corridor);
       setToShowFilter(toShowFilter);
     },
     []
   );

   const filteredByDeviceAndState = useMemo(() => {
    let filtered = supervisions;

    if (deviceFilter.length > 0) {
      filtered = filtered.filter((item) =>
        deviceFilter.includes(item.device_name)
      );
    }

    if (toShowFilter !== 1) {
      if (toShowFilter !== 3) {
        filtered = filtered.filter((entry) => entry.reading_percentage > 0.1);
      } else {
        filtered = filtered.filter((entry) => entry.reading_percentage < 0.1);
      }
    }

    return filtered;
   }, [supervisions, deviceFilter, toShowFilter]);

   const filteredSupervisions = useMemo(() => {
     let filtered = filteredByDeviceAndState;

     if (corridorFilter !== "") {
       filtered = filtered.filter((entry) =>
         entry.corridor_name
           .toLocaleUpperCase()
           .includes(corridorFilter.toLocaleUpperCase())
 
       );
     }
     if (selectedCorridorName !== null) {
      filtered = filtered.filter((entry) => entry.corridor_name === selectedCorridorName);
     }

     return filtered;
   }, [
     filteredByDeviceAndState,
     corridorFilter,
     selectedCorridorName
   ]);

  const selectedSupervision = useMemo(() => {
    if (selectedCorridorName === null) {
      return undefined;
    }

    return filteredByDeviceAndState.find(
      (entry) => entry.corridor_name === selectedCorridorName
    );
  }, [filteredByDeviceAndState, selectedCorridorName]);

  useEffect(() => {
    if (selectedCorridorName === null) {
      return;
    }

    const existsInCurrentData = filteredByDeviceAndState.some(
      (entry) => entry.corridor_name === selectedCorridorName
    );

    if (!existsInCurrentData) {
      setSelectedCorridorName(null);
    }
  }, [filteredByDeviceAndState, selectedCorridorName]);

  useEffect(() => {
    if (
      selectedSupervision === undefined ||
      selectedSupervision.corridor_idx === undefined ||
      warehouseIdx === undefined ||
      sweepIdx === undefined
    ) {
      setSelectedCorridorDetails(undefined);
      return;
    }

    setCorridorMapLoading(true);

    SupervisionService.getSupervisionHistoryDetails(
      parseInt(warehouseIdx),
      parseInt(sweepIdx),
      selectedSupervision.corridor_idx,
      navigate
    )
      .then((details) => {
        setSelectedCorridorDetails(details || undefined);
      })
      .catch((error) => {
        console.error("Failed to fetch selected corridor history details:", error);
        setSelectedCorridorDetails(undefined);
      })
      .finally(() => {
        setCorridorMapLoading(false);
      });
  }, [selectedSupervision, warehouseIdx, sweepIdx, navigate]);

  const updateContent = useCallback(() => {
    if (warehouseIdx === undefined || sweepIdx === undefined) return;
    SupervisionService.getSupervisionHistorySummary(parseInt(warehouseIdx), Number(sweepIdx), navigate)
      .then((supervisions) => {
        setsupervisions(supervisions || []);
        setCorridorsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch supervisions:", error);
        setsupervisions([]);
        setCorridorsLoading(false);
      });
    SupervisionService.getSupervisionHistory(parseInt(sweepIdx), navigate)
      .then((supervisionWarehouse) => {
        setSupervisionWarehouse(supervisionWarehouse || undefined);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch warehouses:", error);
        setLoading(false);
      });
  }, [warehouseIdx, sweepIdx, navigate]);


  useEffect(() => {
    updateContent();
    const interval = setInterval(() => {
      updateContent();
    }, 300000);
    return () => clearInterval(interval);
  }, [updateContent]);
  return (
    <Card>
      <Spin spinning={loading}>
        {supervisionWarehouse && <Card
          title={supervisionWarehouse.warehouse_name}
          extra={<Link to={`/supervision/history/${warehouseIdx}`} >
            <Button
              type="primary"
              shape="round"
              icon={<CaretLeftOutlined />}
            >
              {language === "es" ? "Atrás" : "Back"}
            </Button>
          </Link>}>
          <Row gutter={[10, 10]}>
            {selectedSupervision ? (
              <>
                <Col xs={24} sm={24} md={12} lg={8}>
                  <Card
                    title={language === "es" ? `Rack ${selectedSupervision.corridor_name}` : `Corridor ${selectedSupervision.corridor_name}`}
                    style={{ height: "100%", textAlign: "center" }}
                  >
                    <Row gutter={[8, 10]}>
                      <Col span={24} style={{ textAlign: "center" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 7,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <b style={{ fontSize: screens.lg ? "0.90vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw" }}>{language === "es" ? "Dispositivo:" : "Device:"}</b>
                          <span style={{ fontSize: screens.lg ? "0.90vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw", fontWeight: 500 }}>{selectedSupervision.device_name || "-"}</span>
                          <VideoCameraTwoTone twoToneColor="#ff4d4f" />
                        </span>
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={screens.xs ? 24 : 8} style={{ textAlign: "center" }}>
                  <Card title={language === "es" ? "Lecturas" : "Readings"}>
                    <Row gutter={[10, 15]}>
                      <Col span={24}>
                        <Progress
                          type="line"
                          percent={parseFloat((selectedSupervision.reading_percentage * 100).toFixed(2))}
                          format={(percent) => `${percent}%`}
                          strokeWidth={10}
                          status="active"
                        />
                      </Col>
                      <Col span={23}>
                        <b style={{ fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw" }}>{`${selectedSupervision.read_positions} / ${selectedSupervision.total_count}`}</b>
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={screens.xs ? 24 : 8} style={{ textAlign: "center" }}>
                  <Card title={language === "es" ? "Efectividad" : "Effective"}>
                    <Row gutter={[10, 15]}>
                      <Col span={24}>
                        <Progress
                          type="line"
                          percent={parseFloat((selectedSupervision.effective_percentage * 100).toFixed(2))}
                          format={(percent) => `${percent}%`}
                          strokeWidth={10}
                          status="active"
                          strokeColor={selectedSupervision.effective_percentage > 0.75 ? "#1677ff" : selectedSupervision.effective_percentage > 0.50 ? "#fadb14" : "#f5222d"}
                        />
                      </Col>
                      <Col span={24}>
                        <b style={{ fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw" }}>{`${selectedSupervision.effective_readings} / ${selectedSupervision.read_positions}`}</b>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </>
            ) : (
              <>
                <Col xs={24} sm={24} md={8} lg={8}>
                  <Row gutter={[10, 10]}>
                    <Col span={24} style={{ textAlign: 'center' }} >
                      <Card
                        title={language === "es" ? "General" : "General"}
                        style={{ height: "100%" }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 6,
                              fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw",
                              whiteSpace: "nowrap",
                              textAlign: "center",
                            }}
                          >
                            <b>{language === "es" ? "Numero de conteo:" : "Counting number:"}</b>
                            <span style={{ fontWeight: 500 }}>{supervisionWarehouse.counting_number}</span>
                          </span>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 6,
                              fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw",
                              whiteSpace: "nowrap",
                              textAlign: "center",
                            }}
                          >
                            <b>{language === "es" ? "Cantidad de dispositivos:" : "Devices counting:"}</b>
                            <span style={{ fontWeight: 500 }}>{supervisionWarehouse.devices_count}</span>
                          </span>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </Col>
                <Col span={screens.xs ? 24 : 8} style={{ textAlign: 'center' }}>
                  <Card title={language === "es" ? "Lecturas" : "Readings"}>
                    <Row gutter={[10, 15]}>
                      <Col span={23}>
                        <Progress type="line" percent={parseFloat((supervisionWarehouse.reading_percentage * 100).toFixed(2))}
                          format={(percent) => `${percent}%`} strokeWidth={10} status="active" />
                      </Col>
                      <Col span={24}>
                        <b style={{ fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw" }}>{`${supervisionWarehouse.read_positions} / ${supervisionWarehouse.total_count}`}</b>
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={screens.xs ? 24 : 8} style={{ textAlign: 'center' }}>
                  <Card title={language === "es" ? "Efectividad" : "Effective"}>
                    <Row gutter={[10, 15]}>
                      <Col span={23}>
                        <Progress type="line" percent={parseFloat((supervisionWarehouse.effective_percentage * 100).toFixed(2))}
                          format={(percent) => `${percent}%`} strokeWidth={10} status="active"
                          strokeColor={supervisionWarehouse.effective_percentage > 0.75 ? "#1677ff" : supervisionWarehouse.effective_percentage > 0.50 ? "#fadb14" : "#f5222d"} />
                      </Col>
                      <Col span={24}>
                        <b style={{ fontSize: screens.lg ? "0.80vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw" }}>{`${supervisionWarehouse.effective_readings} / ${supervisionWarehouse.read_positions}`}</b>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </>
            )}
          </Row>
        </Card>}
        <Divider >{language === "es" ? "Racks" : "Corridors"}</Divider>
        <Spin spinning={corridorsLoading && !loading}>
          {supervisions &&
            <>
            <CorridorSelectorMap
              corridors={filteredByDeviceAndState}
              selectedCorridorName={selectedCorridorName}
              onSelect={setSelectedCorridorName}
              getCorridorSeverity={getCorridorSeverity}
            />
            {selectedSupervision && selectedCorridorDetails && sweepIdx !== undefined && (
              <Spin spinning={corridorMapLoading}>
                <Card
                  title={language === "es" ? `Rack ${selectedSupervision.corridor_name}` : `Corridor ${selectedSupervision.corridor_name}`}
                  style={{ marginTop: 8, width: "100%" }}
                  bodyStyle={corridorDetailsCardBodyStyle}
                >
                  <CorridorHistoryMap
                    corridorIdx={String(selectedSupervision.corridor_idx)}
                    sweepIdx={String(sweepIdx)}
                    supervisiondetails={selectedCorridorDetails}
                  />
                </Card>
              </Spin>
            )}
            </>}
        </Spin>
      </Spin>
    </Card>)
}

export default CorridorHistory;